export default interface FeatureFlags {
    /**
     * Allow users to create and view CASI memories.
     */
    allowCasiMemories?: boolean;

    /**
     * Allow users to use CASI to generate checklists.
     */
    allowCasiGeneratedChecklists?: boolean;

    allowOrganizationSSO?: boolean;
    /**
     * Enable sharing muliple sections, plus related MyLink area
     */
    allowShareMultiple?: boolean;

    /**
     * Allow filtering by LegacyPublicationSection entity type
     */
    allowLegacyPublicationSectionFiltering?: boolean;

    /**
     * Allow team/enterprise to share/collaborate on notebooks
     */
    allowNotebookCollaboration?: boolean;

    /**
     * Enable the user role for Technical Committee members.
     */
    allowTechnicalCommitteeAccess?: boolean;

    /**
     * Disable all calls to Hermes. Use when Hermes is down to give users limited access to LiNK.
     */
    bypassHermes?: boolean;

    /**
     * Flag to enable the calculation of publications CAAPS items print order when imported
     */
    calculateCAAPSPrintOrder?: boolean;
    /*
     * Enable prompt suggestions in the CASI panels (notebooks and bookview)
     */
    enableCasiPanelPromptSuggestions?: boolean;

    /**
     * Enables indexing of legacy publications in the NFPA AI Index
     */
    enableLegacyPublicationIndexing?: boolean;

    /**
     * Flag to enable unauthorized handling (redirect to login on 401 responses)
     */
    enableUnauthorizedHandling?: boolean;

    /**
     * Flag for toggling I18N features
     */
    i18N?: boolean;

    /**
     *  Enables using data internal to LiNK to manage teams rather than subscription details from Hermes API
     */

    internalizeTeams?: boolean;

    refreshOnPageChange?: boolean;

    showDeletedChangeDiffs?: boolean;

    showMovedOnChangeIndicatorMenu?: boolean;

    showSSODebugLogs?: boolean;

    /**
     * Show unhandled XML tags with some default styling in book view as an indication
     * that we need to address this XML tag.
     */
    showUnhandledXml?: boolean;

    /** Show the FAQ panel with demo video links */
    showVideoDemoFaq?: boolean;

    /**
     * Flag to enable the AI chat feature.
     */
    useAIChat?: boolean;

    /**
     * Flag to enable the use of CASI context for AI chat.
     */
    useCasiContext?: boolean;

    /**
     * Flag to enable the use of CASI chat in Notebooks.
     */
    useCasiInNotebooks?: boolean;

    /**
     * Flag to enable the new ErrorBoundary around BookViewLayout
     */
    useErrorBoundary?: boolean;

    /**
     * Flag to enable viewing legacy pdf publications and other material
     */
    useDocumentViewer?: boolean;

    /**
     * Use HermesAPI for creating/updating/getting teams info
     */
    useHermesApiForTeams?: boolean;

    /*
     * Use the new MyLiNK view with a combined bookmarks and collections
     */
    useMyLiNKRedesign?: boolean;

    /*
     * Use Netsuite ID instead of Legacy ID to look up user info via Hermes API
     */
    useNetsuiteIdForUserLookup?: boolean;

    /*
     * Allow users to use Notebooks and related features
     */
    useNotebooks?: boolean;

    /**
     * Flag to enable rendering reduction in bookview.
     */
    useReducedRendersInBookview?: boolean;

    /**
     * Flag that enables simple tables(perfect grid 1 cell blocks with 1 set of headers) to look like cards on mobile view
     */
    useSimpleTableCards?: boolean;

    /**
     * Flag that enables using the simple style class for tables
     */
    useSimpleTableStyle?: boolean;

    /**
     * Flag to enable MsalInstance.ssoSilent(). There is sometimes an issue with SSO when going to
     * free access pages. This flag is used to quickly disable ssoSilent().
     */
    useSsoSilent?: boolean;

    /**
     * Flag to enable changing order settings for search results in specific publications
     */
    useSearchResultsOrderSelection?: boolean;

    /**
     * Flag to enable team onboarding flow
     */
    useTeamOnboarding?: boolean;

    /**
     * Flag to enable the use of the prior team selection during onboarding
     */
    useSelectPriorTeamDuringOnboarding?: boolean;

    /**
     * Validate email addresses.  Currently used for the new user form and user creation.
     */
    validateEmailAddresses?: boolean;

    /**
     * Flag to enable the new user dashboard.
     */
    useNewUserDashboard?: boolean;

    // used for tests
    testBooleanFeatureFlag?: boolean;

    useSparkyEvents?: boolean;

    // Flag to enable NFPA AI search provider
    useSparkySearch?: boolean;
}

/**
 * Default values for feature flags.
 * These values should match those on the backend.
 * see FeatureFlags.cs#GetDefaults
 */
export const DEFAULT_FEATURE_FLAGS: Required<FeatureFlags> = {
    allowCasiMemories: false,
    allowCasiGeneratedChecklists: false,
    allowOrganizationSSO: false,
    allowShareMultiple: false,
    allowLegacyPublicationSectionFiltering: false,
    allowNotebookCollaboration: false,
    allowTechnicalCommitteeAccess: false,
    bypassHermes: false,
    calculateCAAPSPrintOrder: false,
    enableCasiPanelPromptSuggestions: false,
    enableLegacyPublicationIndexing: false,
    enableUnauthorizedHandling: false,
    i18N: false,
    internalizeTeams: false,
    refreshOnPageChange: false,
    showDeletedChangeDiffs: false,
    showMovedOnChangeIndicatorMenu: false,
    showSSODebugLogs: false,
    showUnhandledXml: false,
    showVideoDemoFaq: false,
    useAIChat: false,
    useCasiContext: false,
    useCasiInNotebooks: false,
    useDocumentViewer: false,
    useErrorBoundary: false,
    useHermesApiForTeams: false,
    useMyLiNKRedesign: false,
    useNetsuiteIdForUserLookup: false,
    useNotebooks: false,
    useReducedRendersInBookview: false,
    useSimpleTableCards: false,
    useSimpleTableStyle: false,
    useSsoSilent: true,
    useSearchResultsOrderSelection: false,
    useTeamOnboarding: false,
    useSelectPriorTeamDuringOnboarding: false,
    validateEmailAddresses: false,
    useSparkyEvents: false,
    useNewUserDashboard: false,
    useSparkySearch: false,
    // used only for tests and storybook for feature-flag.tsx
    testBooleanFeatureFlag: false,
};
